import React, { useEffect, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  clientLoginSuccess,
  clientLogout,
  selectAuth,
} from "../store/authSlice";
import { selectUsers } from "../store/usersSlice";
import { makeToken } from "../utils/security";
import {
  normalizeUser,
  resolveUserSlug,
  buildUserFullName,
} from "../utils/users";
import { clientLogin as clientLoginRequest } from "../services/portalApi";
import RenderToast from "../components/RenderToast";
import styles from "./ClientPortal.module.css";

const lockDurationMs = 60 * 1000;
const supportEmail = "info@rcmmatter.com";

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem("dp_client_lock") || "{}");
  } catch {
    return {};
  }
};

export default function ClientPortal() {
  const users = useSelector(selectUsers);
  const auth = useSelector(selectAuth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams();
  const [passcode, setPasscode] = useState("");
  const [attempts, setAttempts] = useState(readState().attempts || 0);
  const [lockUntil, setLockUntil] = useState(readState().lockUntil || 0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showPasscode, setShowPasscode] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      const remaining = Math.max(0, lockUntil - Date.now());
      setTimeLeft(remaining);
      if (remaining === 0 && lockUntil) {
        setAttempts(0);
        setLockUntil(0);
        localStorage.removeItem("dp_client_lock");
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockUntil]);

  const locked = lockUntil > Date.now();
  const disableForm = locked || attempts >= 3;
  const sessionClient = normalizeUser(auth.clientSession?.client);
  const client =
    normalizeUser(
      users.find((item) => item.id === auth.clientSession?.clientId),
    ) || sessionClient;
  const clientSlug = client ? resolveUserSlug(client) : "";
  const clientName = client ? buildUserFullName(client) : "";

  const persistLock = (nextAttempts, nextLockUntil) => {
    localStorage.setItem(
      "dp_client_lock",
      JSON.stringify({ attempts: nextAttempts, lockUntil: nextLockUntil }),
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!passcode) {
      RenderToast({ type: "error", message: "Passcode is required." });
      return;
    }

    clientLoginRequest(passcode)
      .then((result) => {
        const user =
          normalizeUser(result.client) ||
          users.find((item) => item.passwordCode === passcode);
        if (!user) {
          throw new Error("Invalid passcode. Please try again.");
        }
        const nextSlug = resolveUserSlug(user || {});
        dispatch(
          clientLoginSuccess({
            token: result.token || makeToken("client"),
            passcode,
            clientId: user.id,
            clientSlug: nextSlug,
            client: user,
          }),
        );
        navigate(`/client/${nextSlug}`, { replace: true });
        RenderToast({
          type: "success",
          message: result.message || "Access granted",
        });
        setPasscode("");
        setAttempts(0);
        setLockUntil(0);
        persistLock(0, 0);
      })
      .catch((error) => {
        const message = error?.message || "Invalid passcode. Please try again.";
        const nextAttempts = attempts + 1;
        setAttempts(nextAttempts);
        persistLock(nextAttempts, lockUntil);
        if (nextAttempts >= 3) {
          const nextLockUntil = Date.now() + lockDurationMs;
          setLockUntil(nextLockUntil);
          persistLock(nextAttempts, nextLockUntil);
          RenderToast({
            type: "error",
            message: "Too many attempts. Try again in 1 minute.",
          });
        } else {
          RenderToast({ type: "error", message });
        }
      });
  };

  useEffect(() => {
    if (!auth.clientSession?.isAuthenticated || !clientSlug) {
      return;
    }

    if (routeSlug !== clientSlug) {
      navigate(`/client/${clientSlug}`, { replace: true });
    }
  }, [auth.clientSession?.isAuthenticated, clientSlug, navigate, routeSlug]);

  const handleClientLogout = () => {
    dispatch(clientLogout());
    navigate("/", { replace: true });
  };

  if (auth.clientSession?.isAuthenticated && client) {
    const iframeHtml = String(client.iframeCode || "").trim();
    const iframeSrcMatch = iframeHtml.match(/src=["']([^"']+)["']/i);
    const iframeSrc = iframeSrcMatch?.[1] || iframeHtml || "about:blank";

    return (
      <div className={styles.viewer}>
        <div className={styles.viewerHeader}>
          <div>
            <img src="/logo/rcmLogo.webp" alt="RCM Portal" />
            <div>
              <h2 className={styles.clientName}>{clientName || "-"}</h2>
            </div>
          </div>
          <button
            type="button"
            className="btnBase btnSecondary"
            onClick={handleClientLogout}
          >
            Logout
          </button>
        </div>
        <div className={styles.frameWrap}>
          <iframe
            src={iframeSrc}
            title={`${clientName || "Client"} portal`}
            className={styles.iframe}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <img
          src="/logo/rcmLogo.webp"
          alt="RCM Portal"
          className={styles.logo}
        />
        <div className={styles.passWrap}>
          <input
            className="inputBase"
            type={showPasscode ? "text" : "password"}
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Enter your passcode"
            disabled={disableForm}
          />
          <button
            type="button"
            className={styles.eye}
            onClick={() => setShowPasscode((v) => !v)}
            disabled={disableForm}
          >
            {showPasscode ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        <button
          type="submit"
          className={`${styles.submit} btnBase btnPrimary`}
          disabled={disableForm}
        >
          Submit
        </button>
        <p className={styles.helperText}>
          Need help? Contact{" "}
          <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
        </p>
        {locked ? (
          <p className={styles.lockText}>
            Passcode is locked. Try again in {Math.ceil(timeLeft / 1000)}{" "}
            seconds.
          </p>
        ) : (
          <p className={styles.lockText}>Attempt {attempts} of 3</p>
        )}
      </form>
    </div>
  );
}
